import { Box, HStack, Table } from "@chakra-ui/react"
import { FaCircleInfo } from "react-icons/fa6"
import { Tooltip } from "../chakra/Tooltip"
import { formatAssetClass } from "../asset/PaginatedTable"
import { COLOR_HIGHLIGHT_BG, COLOR_SOURCE_BADGE_TEXT, getConnectorConfig } from "./constants"

/**
 * Display pieces for a data connection's mapping table, shared so a system's
 * Connections tab and an asset's look the same.
 */

export interface ParsedSourceLabel {
  base: string
  instance: string | null
}

/**
 * Parses a source label matching the pattern "[base name] [N]" into
 * a base name and numeric instance identifier.
 * This will need updated as additional connections are supported
 *
 * Regex breakdown: /^(.*?)\s*\[(\d+)\]\s*$/
 *   ^(.*?)     — capture group 1: the base name (non-greedy, any characters)
 *   \s*        — optional whitespace before the bracket
 *   \[(\d+)\]  — capture group 2: one or more digits inside literal brackets
 *   \s*$       — optional trailing whitespace
 */
export function parseSourceLabel(label: string): ParsedSourceLabel {
  // Groups: [1] = base name, [2] = numeric instance identifier
  const match = /^(.*?)\s*\[(\d+)\]\s*$/.exec(label)
  if (match) {
    return { base: match[1], instance: match[2] }
  }
  return { base: label, instance: null }
}

/** "Editable whilst connection is inactive" / "Uneditable whilst connection is active" */
export function MappingLockTag({ isEditable }: { isEditable: boolean }) {
  return (
    <Box
      px={2}
      py={0.5}
      borderRadius="md"
      fontSize="xs"
      fontWeight="medium"
      bg={isEditable ? "green.subtle" : "orange.subtle"}
      color="primary"
    >
      {isEditable
        ? "Editable whilst connection is inactive"
        : "Uneditable whilst connection is active"}
    </Box>
  )
}

export function SourceInstanceBadge({ instance }: { instance: string }) {
  return (
    <span
      style={{
        fontFamily: "monospace",
        fontWeight: 700,
        color: COLOR_SOURCE_BADGE_TEXT,
        background: COLOR_HIGHLIGHT_BG,
        borderRadius: "4px",
        padding: "1px 5px",
        fontSize: "12px",
        marginLeft: "4px",
      }}
    >
      [{instance}]
    </span>
  )
}

export function AssetTypeLabel({ assetClass }: { assetClass: string | null | undefined }) {
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "1.5",
        whiteSpace: "nowrap",
      }}
    >
      {formatAssetClass(assetClass)}
    </span>
  )
}

/** A mapped source as "Base [N] · Device Source name", struck through when stale. */
export function MappedSourceText({
  sourceLabel,
  sourceName,
  stale = false,
}: {
  sourceLabel: string
  sourceName: string | null | undefined
  stale?: boolean
}) {
  const parsed = parseSourceLabel(sourceLabel)
  return (
    <span style={stale ? { textDecoration: "line-through" } : undefined}>
      {parsed.base}
      {parsed.instance && <SourceInstanceBadge instance={parsed.instance} />}
      {sourceName && <Box as="span" color="fg.muted"> · {sourceName}</Box>}
    </span>
  )
}

/** Asset Name / Asset Type / Source (with the connector's tooltip) / action. */
export function MappingTableHeader({ connectorType }: { connectorType: string }) {
  const connectorConfig = getConnectorConfig(connectorType)
  return (
    <Table.Header>
      <Table.Row>
        <Table.ColumnHeader>Asset Name</Table.ColumnHeader>
        <Table.ColumnHeader>Asset Type</Table.ColumnHeader>
        <Table.ColumnHeader>
          <HStack gap={1} align="center">
            <span>Source</span>
            <Tooltip
              content={connectorConfig.sourceTooltip}
              contentProps={{ css: { "--tooltip-bg": "white", color: "primary", textTransform: "none" } }}
            >
              <Box color="fg.muted" cursor="help" display="inline-flex">
                <FaCircleInfo style={{ fontSize: "11px" }} />
              </Box>
            </Tooltip>
          </HStack>
        </Table.ColumnHeader>
        <Table.ColumnHeader></Table.ColumnHeader>
      </Table.Row>
    </Table.Header>
  )
}
